import { Link, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface SettingsCard {
  title: string;
  description: string;
  to: string;
  adminOnly?: boolean;
}

const buildCards = (slug: string): SettingsCard[] => [
  {
    title: "Members",
    description: "Invite team members, change roles, remove access.",
    to: `/o/${slug}/settings/members`,
    adminOnly: true,
  },
  {
    title: "Branding",
    description: "Upload your light logo, dark logo and favicon for tenant pages.",
    to: `/o/${slug}/settings/branding`,
    adminOnly: true,
  },
  {
    title: "Billing tier",
    description: "Company size band that drives your subscription pricing.",
    to: `/o/${slug}/settings/billing-tier`,
    adminOnly: true,
  },
  {
    title: "Billing",
    description: "Modules, subscription status, payment method, invoices.",
    to: `/o/${slug}/billing`,
    adminOnly: true,
  },
  {
    title: "Profile and preferences",
    description: "Theme (light, dark, system), Coach guidance toggle.",
    to: `/o/${slug}/settings/profile`,
    adminOnly: false,
  },
];

export function Settings() {
  const { slug } = useParams<{ slug: string }>();
  const { activeOrganisation } = useAuth();
  const isAdmin = activeOrganisation?.role === "Admin";
  if (!slug) return null;

  const cards = buildCards(slug).filter((c) => !c.adminOnly || isAdmin);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Settings</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Configure your organisation and your personal preferences.
        </p>
      </div>
      <ul className="grid gap-3 sm:grid-cols-2">
        {cards.map((card) => (
          <li key={card.to}>
            <Link
              to={card.to}
              className="block h-full rounded-lg border border-slate-200 bg-white p-4 transition hover:border-brand-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-brand-400"
            >
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {card.title}
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{card.description}</p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
